import { Chip } from "@mui/material";
import { ReceiptLong } from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import CustomDataGrid from "../../components/ui/customDataGrid/customDataGrid.component";
import {
  INVOICES_ENDPOINT,
  INVOICE_STATUS_COLOR_MAP,
  INVOICE_STATUS_LABEL_KEYS,
  INVOICE_STATUS_OPTIONS,
  KSEF_STATUS_COLOR_MAP,
  KSEF_STATUS_LABEL_KEYS,
  KSEF_STATUS_OPTIONS,
} from "../../consts";
import { useHasPermission } from "../../contexts/auth";
import {
  clearSelectedInvoices,
  setSelectedInvoices,
  useSelectedInvoices,
} from "../../hooks/useSelectedInvoices";
import { formatNumber } from "../../utils";
import {
  containsStringFilterOperators,
  dateRangeFilterOperators,
  equalsNumberFilterOperators,
  equalsStringFilterOperators,
  singleSelectFilterOperators,
} from "../../components/ui/customDataGrid/filterOperators";

export default function InvoicesList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const canViewInvoices = useHasPermission("invoice.view_invoice");
  const canSend = useHasPermission("invoice.send_invoice");
  const canSubmitToKsef = useHasPermission("invoice.submit_invoice_to_ksef");
  const selectedInvoices = useSelectedInvoices();

  // The selection belongs to this visit to the list: opening an invoice and
  // coming back starts with nothing ticked.
  useEffect(() => () => clearSelectedInvoices(), []);

  // Quick filters live in the URL so a filtered view can be shared or
  // bookmarked; the toolbar writes them, this page reads them back.
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const paidParam = params.get("is_paid") || "";
  const overdueParam = params.get("overdue") || "";

  const extraParams = useMemo(() => {
    const result: Record<string, string | undefined> = {};
    if (paidParam) {
      result.is_paid = paidParam;
    }
    if (overdueParam) {
      result.overdue = overdueParam;
    }
    return result;
  }, [paidParam, overdueParam]);

  const STATUS_OPTIONS = useMemo(
    () => INVOICE_STATUS_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );
  const KSEF_OPTIONS = useMemo(
    () => KSEF_STATUS_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );

  const columns: GridColDef[] = [
    {
      field: "number",
      headerName: t("Invoice number"),
      minWidth: 150,
      filterOperators: containsStringFilterOperators(),
      // Drafts have no number yet; the Status column already labels them.
      valueGetter: (value) => value || "—",
    },
    {
      field: "status",
      headerName: t("Status"),
      minWidth: 110,
      type: "singleSelect",
      valueOptions: STATUS_OPTIONS,
      filterOperators: singleSelectFilterOperators(),
      valueGetter: (value, row) => row.status,
      renderCell: (params) => (
        <Chip
          size="small"
          label={t(INVOICE_STATUS_LABEL_KEYS[params.row.status] ?? params.row.status)}
          color={INVOICE_STATUS_COLOR_MAP[params.row.status] ?? "default"}
        />
      ),
    },
    {
      field: "buyer_name",
      headerName: t("Buyer"),
      minWidth: 220,
      flex: 1,
      filterOperators: containsStringFilterOperators(),
    },
    {
      field: "buyer_nip",
      headerName: "NIP",
      minWidth: 110,
      sortable: false,
      filterOperators: equalsStringFilterOperators(),
    },
    {
      field: "issued_at",
      headerName: t("Date of issue"),
      minWidth: 120,
      filterOperators: dateRangeFilterOperators(),
    },
    {
      field: "due_date",
      headerName: t("Payment due"),
      minWidth: 130,
      filterOperators: dateRangeFilterOperators(),
      renderCell: (params) =>
        params.row.is_overdue ? (
          <Chip size="small" color="error" label={params.row.due_date} />
        ) : (
          params.row.due_date
        ),
    },
    {
      field: "total",
      headerName: t("Gross value"),
      minWidth: 130,
      align: "right",
      headerAlign: "right",
      type: "number",
      filterOperators: equalsNumberFilterOperators(),
      valueFormatter: (value) => `${formatNumber(value)} zł`,
    },
    {
      field: "is_paid",
      headerName: t("Payment"),
      minWidth: 130,
      type: "boolean",
      renderCell: (params) => (
        <Chip
          size="small"
          color={params.row.is_paid ? "success" : "warning"}
          label={params.row.is_paid ? t("Paid") : t("Awaiting payment")}
        />
      ),
    },
    {
      field: "ksef_status",
      headerName: "KSeF",
      minWidth: 120,
      type: "singleSelect",
      valueOptions: KSEF_OPTIONS,
      filterOperators: singleSelectFilterOperators(),
      valueGetter: (value, row) => row.ksef_status,
      renderCell: (params) => (
        <Chip
          size="small"
          label={t(KSEF_STATUS_LABEL_KEYS[params.row.ksef_status] ?? params.row.ksef_status)}
          color={KSEF_STATUS_COLOR_MAP[params.row.ksef_status] ?? "default"}
        />
      ),
    },
  ];

  const handleRowClick = (id: string) => {
    navigate(`/invoices/${id}`);
  };

  if (!canViewInvoices) {
    return null;
  }

  return (
    <CustomDataGrid
      icon={<ReceiptLong />}
      title={t("Sales invoices list")}
      url={INVOICES_ENDPOINT}
      gridSettingsKey="INVOICES"
      columns={columns}
      extraParams={extraParams}
      defaultHiddenFields={["buyer_nip"]}
      refreshEventName={["invoiceAdded", "invoiceUpdated"]}
      onRowClick={handleRowClick}
      // Checkboxes only for someone who can act on a selection.
      rowSelection={
        canSend || canSubmitToKsef
          ? { selectedRows: selectedInvoices, onChange: setSelectedInvoices }
          : undefined
      }
    />
  );
}
